import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { HOME } from "@/constants/testIds";

export default function VehicleFinder() {
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const nav = useNavigate();
  const { t } = useLang();

  useEffect(() => {
    api.get("/vehicles/brands").then((r) => setBrands(r.data.brands || []));
  }, []);

  useEffect(() => {
    if (!brand) { setModels([]); setModel(""); return; }
    api.get("/vehicles/models", { params: { brand } }).then((r) => setModels(r.data.models || []));
    setModel("");
  }, [brand]);

  const submit = () => {
    const params = new URLSearchParams();
    if (brand) params.set("brand", brand);
    if (model) params.set("model", model);
    nav(`/catalogo?${params.toString()}`);
  };

  return (
    <div className="vf-bezel w-full max-w-xl mx-auto">
      {/* bezel top bar with LED and label */}
      <div className="flex items-center justify-between mb-4 px-1">
        <div className="flex items-center gap-2">
          <span className="vf-led" />
          <span className="font-mono-tech text-[10px] tracking-widest text-xd-cyan uppercase">Vehicle Finder</span>
        </div>
        <div className="font-mono-tech text-[10px] text-slate-500">v2.6 · online</div>
      </div>

      <div className="vf-screen relative">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-display text-white text-xl leading-tight">{t("finder_console_title")}</div>
            <div className="text-slate-400 text-xs mt-1 font-mono-tech">{t("finder_console_sub")}</div>
          </div>
          <div className="font-mono-tech text-[10px] text-xd-cyan/70">{new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 relative z-10">
          <div>
            <label className="block text-[10px] font-mono-tech uppercase tracking-widest text-slate-400 mb-1">{t("finder_label_brand")}</label>
            <select
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              data-testid={HOME.finderBrand}
              className="w-full appearance-none bg-xd-dark border border-xd-darkBorder text-white rounded-lg px-3 py-3 text-sm focus:outline-none focus:border-xd-cyan focus:ring-1 focus:ring-xd-cyan cursor-pointer"
            >
              <option value="">{t("finder_placeholder_brand")}</option>
              {brands.map((b) => (<option key={b} value={b}>{b}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono-tech uppercase tracking-widest text-slate-400 mb-1">{t("finder_label_model")}</label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              disabled={!brand}
              data-testid={HOME.finderModel}
              className="w-full appearance-none bg-xd-dark border border-xd-darkBorder text-white rounded-lg px-3 py-3 text-sm focus:outline-none focus:border-xd-cyan focus:ring-1 focus:ring-xd-cyan cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <option value="">{brand ? t("finder_placeholder_model") : t("finder_placeholder_model_pre")}</option>
              {models.map((m) => (<option key={m} value={m}>{m}</option>))}
            </select>
          </div>
        </div>

        <button
          type="button"
          onClick={submit}
          data-testid={HOME.finderSubmit}
          className="mt-4 w-full btn-cta rounded-lg px-5 py-3 text-sm inline-flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-xd-orange/50"
        >
          <Search className="w-4 h-4" />
          {t("finder_cta")}
        </button>

        <div className="mt-3 flex items-center justify-between text-[10px] font-mono-tech text-slate-500">
          <span>{t("finder_foot_kit")}</span>
          <span>{t("finder_foot_ship")}</span>
          <span>{t("finder_foot_warranty")}</span>
        </div>
      </div>

      {/* physical knobs bottom */}
      <div className="mt-4 flex items-center justify-between px-2">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-slate-600 to-slate-800 shadow-inner border border-slate-900" />
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-slate-600 to-slate-800 shadow-inner border border-slate-900" />
        </div>
        <div className="font-mono-tech text-[10px] text-slate-600">audrivio · CAR HEAD UNIT</div>
      </div>
    </div>
  );
}
